import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import {
    WdxButtonSize,
    WdxButtonStyle,
} from '@wdx/shared/components/wdx-button';
import {
    ActionType,
    EntityAction,
    LogicStepType,
} from '@wdx/shared/swagger-gen';
import {
    NodeAddedEvent,
    WorkflowNode,
    WorkflowNodeChildren,
} from '../../models';
import {
    WdxDialogComponent,
    WdxDialogService,
} from '@wdx/shared/components/wdx-dialog';
import { TranslationsService } from '@wdx/shared/utils';
import { WorkflowNodeHelperService } from '../../services';
@Component({
    selector: 'clmi-workflow-designer-visual',
    templateUrl: './workflow-designer-visual.component.html',
    styleUrl: './workflow-designer-visual.component.scss',
    standalone: false,
})
export class WorkflowDesignerVisualComponent {
    private readonly translationsService = inject(TranslationsService);
    private readonly wdxDialogService = inject(WdxDialogService);
    private readonly nodeHelper = inject(WorkflowNodeHelperService);

    @Input() workflowNodes: WorkflowNode[];
    @Input() activeNodeId?: string;
    @Output() addNode = new EventEmitter<NodeAddedEvent>();
    @Output() removeNode = new EventEmitter<WorkflowNode>();
    @Output() selectNode = new EventEmitter<WorkflowNode | null>();

    readonly WdxButtonSize = WdxButtonSize;
    readonly WdxButtonStyle = WdxButtonStyle;
    readonly LogicStepType = LogicStepType;

    readonly actions: Array<
        EntityAction & {
            code: string;
            isDisabled: (node: WorkflowNode) => boolean;
        }
    > = [
        {
            code: 'remove',
            icon: 'trash',
            displayName: {
                key: 'MENU_REMOVE',
            },
            isDisabled: (node) => !node.isRemovable,
        },
    ];
    opened = false;

    public onAddClick(
        node?: WorkflowNode,
        logicCode?: string,
        isNewLogic?: boolean,
    ) {
        const nodeAdded = {
            parentId: node?.payload?.Id ?? 'root',
            logicCode: logicCode ?? node?.logicCode,
            isNewLogic: isNewLogic,
        } as NodeAddedEvent;

        this.addNode.emit(nodeAdded);

        this.activeNodeId = this.buildActiveNodeId(
            nodeAdded.parentId,
            nodeAdded.logicCode,
            nodeAdded.isNewLogic,
        );
    }

    public onNodeClick(node: WorkflowNode) {
        this.selectNode.emit(node);

        this.activeNodeId = this.buildActiveNodeId(
            node.payload.Id,
            node.logicCode,
        );
    }

    public containerClick() {
        this.selectNode.emit(null);
        this.activeNodeId = null;
    }

    public hasChildNodes(children: WorkflowNodeChildren): boolean {
        return children && Object.keys(children).length > 0;
    }

    public actionClicked(
        action: EntityAction & { code: string },
        node: WorkflowNode,
    ) {
        if (action.code === 'remove') {
            this.confirmRemove(node);
        }
    }

    public openChange(opened: boolean) {
        this.opened = opened;
    }

    public getLogicColour(key: string): string {
        switch (key) {
            case 'Then':
                return 'bg-success';
            case 'Else':
                return 'bg-danger';
            case 'ForEach':
                return 'bg-primary';
            default:
                return 'bg-info';
        }
    }

    public buildActiveNodeId(
        nodeId: string,
        logicCode?: string,
        isNew?: boolean,
    ) {
        const activeNodeId = this.nodeHelper.buildActiveNodeId(
            nodeId,
            logicCode,
        );
        return isNew ? `${activeNodeId}-new` : activeNodeId;
    }

    private confirmRemove(node: WorkflowNode) {
        const deleteDialog = this.wdxDialogService.open(WdxDialogComponent);
        deleteDialog.componentInstance.title =
            this.translationsService.translateTokenisedString(
                'WORKFLOW_CONFIRM_REMOVE_TITLE',
            );
        deleteDialog.componentInstance.dialogBody =
            this.translationsService.translateTokenisedString(
                'WORKFLOW_CONFIRM_REMOVE_DESCRIPTION',
            );
        deleteDialog.componentInstance.wdxOnClick.subscribe(() => {
            this.removeNode.emit(node);
        });
    }
}
